import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, Phone, MapPin, Users, Heart, Calendar,
  MessageSquare, KeyRound, ClipboardList, AlertTriangle, ShieldCheck, Pencil
} from "lucide-react";

const Field = ({ label, value, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-stone2-500">{label}</div>
    <div className="mt-0.5 text-sm text-sage-900" data-testid={testId}>{value || <span className="text-stone2-400">—</span>}</div>
  </div>
);

export default function AdminFamilyDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/families/${encodeURIComponent(userId)}`);
      setData(data);
    } catch (e) { const m = extractError(e); setError(m); toast.error(m); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const resetPassword = async () => {
    if (!window.confirm(`Generate a temporary password for ${data.user.email}?\n\nThey will be forced to change it on next login.`)) return;
    setResetting(true);
    try {
      const { data: r } = await api.post(`/admin/users/${userId}/reset-password`);
      try { await navigator.clipboard.writeText(r.temporary_password); } catch (e) {}
      window.alert(`Temporary password for ${r.email}:\n\n    ${r.temporary_password}\n\n(copied to your clipboard)\n\nShare it with them. They'll be forced to change it on first login.`);
      toast.success("Password reset — copied to clipboard");
    } catch (e) { toast.error(extractError(e)); }
    finally { setResetting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-14">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </Link>
          <div className="mt-6 paper-card p-8 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto text-danger" />
            <p className="mt-3 text-sage-900 font-heading font-semibold">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="p-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-sage-600" /></div>
      </div>
    );
  }

  const { user, profile, children, listings, booking_count } = data;
  const familyDisplay = profile.family_name || profile.parent_name || user.name || "(no name)";
  const location = [profile.city, profile.state, profile.zip_code].filter(Boolean).join(", ") || "—";
  const emergencyContacts = profile.emergency_contacts || [];
  const pets = profile.pets || [];

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="admin-family-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        {/* Header */}
        <div className="mt-3 paper-card p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              {profile.photo_path ? (
                <img src={`/api/uploads/${profile.photo_path}`} alt=""
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-sage-100" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
                  {familyDisplay[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-heading font-bold text-sage-900" data-testid="admin-family-name">{familyDisplay}</h1>
                <div className="mt-1 flex items-center gap-3 text-sm text-stone2-600 flex-wrap">
                  <a href={`mailto:${user.email}`} className="inline-flex items-center gap-1 text-sage-700 hover:text-sage-900">
                    <Mail className="w-3.5 h-3.5" /> {user.email}
                  </a>
                  {(user.phone_number || profile.parent_phone) && (
                    <a href={`tel:${user.phone_number || profile.parent_phone}`} className="inline-flex items-center gap-1 text-sage-700 hover:text-sage-900">
                      <Phone className="w-3.5 h-3.5" /> {user.phone_number || profile.parent_phone}
                      {user.sms_opt_in && <span className="ml-1 text-[10px] font-bold text-sage-700">SMS✓</span>}
                    </a>
                  )}
                  <span className="inline-flex items-center gap-1 text-stone2-500">
                    <MapPin className="w-3.5 h-3.5" /> {location}
                  </span>
                </div>
                <div className="mt-1 text-xs text-stone2-500">
                  Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                  {" · "}{booking_count} {booking_count === 1 ? "booking" : "bookings"}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button type="button"
                onClick={() => {
                  const p = new URLSearchParams({ admin_edit: userId });
                  window.location.href = `/family/profile?${p.toString()}`;
                }}
                data-testid="admin-family-edit"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-sage-300 text-sage-800 hover:bg-sage-50">
                <Pencil className="w-3.5 h-3.5" /> Edit profile
              </button>
              <button type="button" onClick={resetPassword} disabled={resetting}
                data-testid="admin-family-reset-pw"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-stone2-200 text-sage-800 hover:bg-sage-50 disabled:opacity-50">
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                Reset password
              </button>
            </div>
          </div>
        </div>

        {/* Parent / household */}
        <section className="mt-6 paper-card p-6" data-testid="admin-family-household">
          <h2 className="font-heading font-semibold text-sage-900 text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-sage-600" /> Household
          </h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="Family name" value={profile.family_name} testId="fam-detail-family-name" />
            <Field label="Primary parent" value={profile.parent_name} testId="fam-detail-parent-name" />
            <Field label="Parent phone" value={profile.parent_phone || user.phone_number} testId="fam-detail-parent-phone" />
            <Field label="Address" value={profile.address} testId="fam-detail-address" />
          </div>
          {profile.household_notes && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-stone2-500">Household notes</div>
              <p className="mt-0.5 text-sm text-sage-900 leading-relaxed">{profile.household_notes}</p>
            </div>
          )}
        </section>

        {/* Children */}
        <section className="mt-6 paper-card p-6" data-testid="admin-family-children">
          <h2 className="font-heading font-semibold text-sage-900 text-lg flex items-center gap-2">
            <Heart className="w-5 h-5 text-sage-600" /> Children ({children.length})
          </h2>
          {children.length === 0 ? (
            <p className="mt-2 text-sm text-stone2-500">No children on file.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {children.map((c) => (
                <li key={c.id} className="rounded-xl border border-stone2-200 bg-white p-4" data-testid={`admin-family-child-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-sage-900">{c.name || "Unnamed child"}</span>
                    {c.age !== null && c.age !== undefined && <span className="chip">{c.age} yrs</span>}
                    {c.diagnosis && <span className="chip-ochre">{c.diagnosis}</span>}
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                    {c.triggers && (
                      <div><strong className="text-sage-900">Triggers:</strong> <span className="text-stone2-700">{c.triggers}</span></div>
                    )}
                    {c.calming_strategies && (
                      <div><strong className="text-sage-900">Calming strategies:</strong> <span className="text-stone2-700">{c.calming_strategies}</span></div>
                    )}
                    {c.interests && (
                      <div><strong className="text-sage-900">Interests:</strong> <span className="text-stone2-700">{c.interests}</span></div>
                    )}
                    {c.communication_style && (
                      <div><strong className="text-sage-900">Communication:</strong> <span className="text-stone2-700">{c.communication_style}</span></div>
                    )}
                    {c.medications && (
                      <div><strong className="text-sage-900">Medications:</strong> <span className="text-stone2-700">{c.medications}</span></div>
                    )}
                    {c.allergies && (
                      <div><strong className="text-sage-900">Allergies:</strong> <span className="text-stone2-700">{c.allergies}</span></div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Emergency contacts + pets */}
        {(emergencyContacts.length > 0 || pets.length > 0) && (
          <section className="mt-6 grid sm:grid-cols-2 gap-6">
            {emergencyContacts.length > 0 && (
              <div className="paper-card p-6" data-testid="admin-family-emergency-contacts">
                <h3 className="font-heading font-semibold text-sage-900 text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-sage-600" /> Emergency contacts
                </h3>
                <ul className="mt-3 space-y-2">
                  {emergencyContacts.map((c, i) => (
                    <li key={c._uid || i} className="text-sm text-sage-900">
                      <div className="font-semibold">{c.name} <span className="text-stone2-500 font-normal">· {c.relationship}</span></div>
                      <div className="text-xs text-stone2-600">
                        {c.phone && <a href={`tel:${c.phone}`} className="text-sage-700 hover:text-sage-900 mr-3">{c.phone}</a>}
                        {c.email && <a href={`mailto:${c.email}`} className="text-sage-700 hover:text-sage-900">{c.email}</a>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pets.length > 0 && (
              <div className="paper-card p-6" data-testid="admin-family-pets">
                <h3 className="font-heading font-semibold text-sage-900 text-lg">Pets</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {pets.map((p, i) => (
                    <li key={p._uid || i} className="text-sage-900">
                      <strong>{p.name || "Unnamed"}</strong> <span className="text-stone2-500">· {p.type}</span>
                      {p.notes && <div className="text-xs text-stone2-600">{p.notes}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Listings + booking history */}
        <section className="mt-6 paper-card p-6" data-testid="admin-family-listings">
          <h2 className="font-heading font-semibold text-sage-900 text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-sage-600" /> Care listings ({listings.length})
          </h2>
          {listings.length === 0 ? (
            <p className="mt-2 text-sm text-stone2-500">This family hasn't posted a care listing yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {listings.map((l) => (
                <li key={l.id} className="rounded-xl border border-stone2-200 bg-white p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-heading font-semibold text-sage-900">{l.title || "Untitled listing"}</span>
                    <span className={`chip ${l.status === "open" ? "chip-ochre" : ""}`}>{l.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-stone2-500">
                    Posted {new Date(l.created_at).toLocaleDateString()}
                    {" · "}{l.view_count || 0} views
                    {" · "}{l.message_count || 0} messages
                  </div>
                  {l.description && <p className="mt-2 text-sm text-stone2-700 line-clamp-3">{l.description}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
